import { useState } from 'react'

import type { AccessEvento, AccessRilevamento, AccessStatoAccount } from '../../api/types'
import { t } from '../../i18n/lingua'
import { formatDateTime, timeAgo } from '../common'
import type { Column } from '../DataTable'

/**
 * CENSO ACCESS (ADR-0030): the labels and small pieces every view of the
 * area shares — what an action, a source, a logon type mean in words, the
 * badges of outcome and severity, the 24-hour chart of the overview.
 */

export function etichettaAzione(a: string): string {
  const mappa: Record<string, string> = {
    logon: t('Accesso'),
    logon_failed: t('Accesso fallito'),
    logoff: t('Uscita'),
    privilege_use: t('Uso di privilegi'),
    explicit_credentials: t('Credenziali esplicite'),
    account_change: t('Modifica di un account'),
    group_change: t('Modifica di un gruppo'),
    role_change: t('Modifica di un ruolo'),
    privilege_change: t('Variazione di un privilegio'),
    audit_change: t('Modifica della politica di audit'),
    log_cleared: t('Registro cancellato'),
    config_change: t('Modifica della configurazione'),
  }
  return mappa[a] ?? a
}

export function etichettaFonte(f: string): string {
  const mappa: Record<string, string> = {
    windows: t('Windows'),
    linux: t('Linux'),
    dc: t('Domain controller'),
    entra: t('Entra ID'),
    azure: t('Azure'),
    network: t('Apparati di rete'),
    wifi: t('Wi‑Fi'),
    censo: t('CENSO ACCESS'),
  }
  return mappa[f] ?? f
}

export function etichettaTipoAccesso(tipo: string | null): string {
  if (!tipo) return '—'
  const mappa: Record<string, string> = {
    interactive: t('interattivo'),
    remote: t('desktop remoto'),
    network: t('di rete'),
    ssh: t('SSH'),
    console: t('console'),
    web: t('web'),
    api: t('API'),
    vpn: t('VPN'),
    wifi: t('Wi‑Fi'),
    service: t('servizio'),
    batch: t('attività pianificata'),
    unlock: t('sblocco'),
    cached: t('credenziali in cache'),
    sudo: t('sudo'),
    su: t('su'),
    other: t('altro'),
  }
  return mappa[tipo] ?? tipo
}

export function etichettaTipoIdentita(k: string): string {
  const mappa: Record<string, string> = {
    person: t('persona'),
    service: t('account di servizio'),
    shared: t('account condiviso'),
    builtin: t('account predefinito'),
    unknown: t('non classificato'),
  }
  return mappa[k] ?? k
}

export function etichettaGravita(g: AccessRilevamento['severity']): string {
  return { low: t('bassa'), medium: t('media'), high: t('alta'), critical: t('critica') }[g] ?? g
}

export function BadgeGravita({ g }: { g: AccessRilevamento['severity'] }) {
  const tono = g === 'critical' || g === 'high' ? 'error' : g === 'medium' ? 'warn' : 'muted'
  return <span className={`badge badge--${tono}`}>{etichettaGravita(g)}</span>
}

export function BadgeEvento({ e }: { e: AccessEvento }) {
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {e.outcome === 'failure'
        ? <span className="badge badge--error">{t('fallito')}</span>
        : <span className="badge badge--ok">{t('riuscito')}</span>}
      {e.privileged && <span className="badge badge--warn">{t('privilegiato')}</span>}
      {e.off_hours && <span className="badge badge--info">{t('fuori orario')}</span>}
      {e.mfa === false && <span className="badge badge--error">{t('senza MFA')}</span>}
    </span>
  )
}

/**
 * The last 24 hours, one stacked bar per hour: successes below, failures on
 * top. One scale for both; the hover says the hour and the two counts.
 */
export function GraficoOre({ punti }: { punti: { hour: string; success: number; failure: number }[] }) {
  const [sopra, setSopra] = useState<number | null>(null)
  if (punti.length === 0) return <p className="muted" style={{ margin: 0 }}>{t('Nessun evento nelle ultime 24 ore.')}</p>
  const larghezza = 720
  const altezza = 150
  const margine = { sx: 36, dx: 8, su: 8, giu: 22 }
  const massimo = Math.max(1, ...punti.map((p) => p.success + p.failure))
  const passo = (larghezza - margine.sx - margine.dx) / punti.length
  const y = (v: number) => altezza - margine.giu - (v / massimo) * (altezza - margine.su - margine.giu)
  const ora = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  const p = sopra !== null ? punti[sopra] : null
  return (
    <div className="accgrafico">
      <svg viewBox={`0 0 ${larghezza} ${altezza}`} role="img" aria-label={t('Accessi delle ultime 24 ore per ora')} style={{ width: '100%', height: 'auto' }}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={margine.sx} x2={larghezza - margine.dx} y1={y(massimo * f)} y2={y(massimo * f)} className="accgrafico__griglia" />
            <text x={margine.sx - 6} y={y(massimo * f) + 4} textAnchor="end" className="accgrafico__asse">{Math.round(massimo * f)}</text>
          </g>
        ))}
        {punti.map((pt, i) => {
          const x = margine.sx + i * passo + 1
          const w = Math.max(1, passo - 2)
          return (
            <g key={pt.hour} onMouseEnter={() => setSopra(i)} onMouseLeave={() => setSopra(null)}>
              <rect x={x - 1} y={margine.su} width={passo} height={altezza - margine.su - margine.giu} fill="transparent" />
              <rect x={x} y={y(pt.success)} width={w} height={Math.max(0, y(0) - y(pt.success))} className="accgrafico__ok" rx={1.5} />
              {pt.failure > 0 && (
                <rect x={x} y={y(pt.success + pt.failure)} width={w} height={Math.max(0, y(pt.success) - y(pt.success + pt.failure) - 1)} className="accgrafico__ko" rx={1.5} />
              )}
              {i % 4 === 0 && <text x={x + w / 2} y={altezza - 6} textAnchor="middle" className="accgrafico__asse">{ora(pt.hour)}</text>}
            </g>
          )
        })}
      </svg>
      <div className="accgrafico__legenda">
        <span><i className="accgrafico__punto accgrafico__punto--ok" />{t('riusciti')}</span>
        <span><i className="accgrafico__punto accgrafico__punto--ko" />{t('falliti')}</span>
        {p && <span className="muted">{ora(p.hour)} · {t('{n} riusciti', { n: p.success })} · {t('{n} falliti', { n: p.failure })}</span>}
      </div>
    </div>
  )
}

/** The columns of the accounts' state (Persone, Privilegi): active or
 * disabled, last logon, password that never expires, last password change. */
export function colonneStatoAccount<T extends AccessStatoAccount>(): Column<T>[] {
  return [
    { key: 'account_enabled', header: t('Account'), filter: 'select',
      value: (r) => (r.account_enabled === true ? t('attivo') : r.account_enabled === false ? t('disattivato') : ''),
      render: (r) => r.account_enabled === true
        ? <span className="badge badge--ok">{t('attivo')}{r.account_locked ? ` · ${t('bloccato')}` : ''}</span>
        : r.account_enabled === false ? <span className="badge">{t('disattivato')}</span>
          : <span className="muted" title={t('Lo stato lo dice il sistema dell’account: il domain controller per il dominio, la macchina per gli account locali (agente aggiornato).')}>—</span> },
    { key: 'last_logon', header: t('Ultimo accesso'), type: 'date', value: (r) => r.last_logon ?? '',
      render: (r) => r.last_logon ? <span title={formatDateTime(r.last_logon)}>{timeAgo(r.last_logon)}</span> : <span className="muted">—</span> },
    { key: 'password_never_expires', header: t('Password che non scade'), filter: 'select',
      value: (r) => (r.password_never_expires === true ? t('sì') : r.password_never_expires === false ? t('no') : ''),
      render: (r) => r.password_never_expires === true ? <span className="badge badge--warn">{t('non scade')}</span>
        : r.password_never_expires === false ? t('scade') : <span className="muted">—</span> },
    { key: 'password_last_set', header: t('Ultimo cambio password'), type: 'date', value: (r) => r.password_last_set ?? '',
      render: (r) => r.password_last_set ? <span title={formatDateTime(r.password_last_set)}>{timeAgo(r.password_last_set)}</span> : <span className="muted">—</span> },
  ]
}
